/**
 * Payment Store
 *
 * Manages payment status and PDF download state.
 * Replaces Alpine.store('app') payment-related properties.
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'
import { paymentService } from '@/services/payment.service'
import { authService } from '@/services/auth.service'

export const usePaymentStore = defineStore('payment', () => {
  // ==================== STATE ====================

  /**
   * Has user purchased the appeal pack?
   * Replaces: Alpine.store('app').hasPurchasedPack
   */
  const hasPurchased = ref(false)

  /**
   * Payment status after returning from Stripe
   * Values: null (no payment), 'success', 'cancelled'
   * Replaces: Alpine.store('app').paymentStatus
   */
  const paymentStatus = ref(null)

  /**
   * Appeal data (property segments and comp data)
   * Loaded after purchase for display in My Appeal section
   */
  const appealData = ref([])

  /**
   * Is PDF currently being downloaded?
   */
  const isDownloadingPDF = ref(false)

  /**
   * Eligibility check reason (set when ok_to_purchase is false)
   */
  const eligibilityReason = ref('')
  const eligibilityComps = ref(0)

  /**
   * Is an eligibility check currently in progress?
   */
  const isCheckingEligibility = ref(false)

  // ==================== ACTIONS ====================

  /**
   * Create Stripe Checkout Session
   * Redirects user to Stripe payment page
   */
  async function createCheckout() {
    try {
      const response = await paymentService.createCheckoutSession()

      // Redirect to Stripe checkout page
      // Backend returns 'url' field
      const checkoutUrl = response.data.url || response.data.checkout_url

      if (checkoutUrl) {
        window.location.href = checkoutUrl
      } else {
        throw new Error('No checkout URL in response')
      }

    } catch (error) {
      console.error('Failed to create checkout session:', error)
      throw error
    }
  }

  /**
   * Check Purchase Eligibility
   * Returns true if eligible, false if not (sets eligibilityReason)
   */
  async function checkEligibility() {
    isCheckingEligibility.value = true
    try {
      const response = await paymentService.checkEligibility()
      const { ok_to_purchase, reason, num_comps } = response.data
      eligibilityReason.value = reason
      eligibilityComps.value = num_comps || 0
      return ok_to_purchase
    } catch (error) {
      console.error('Eligibility check failed:', error)
      // On error, allow purchase (don't block on infrastructure issues)
      return true
    } finally {
      isCheckingEligibility.value = false
    }
  }

  /**
   * Poll Payment Status - Check if payment completed
   * Replaces: Alpine.store('app').checkPaymentStatus()
   *
   * Called after returning from Stripe checkout.
   * Polls the session status endpoint with retry logic.
   *
   * @param {number} maxAttempts - Maximum polling attempts (default: 3)
   * @param {number} pollInterval - Milliseconds between polls (default: 1000)
   * @returns {Promise<boolean>} - True if payment confirmed
   */
  async function pollPaymentStatus(maxAttempts = 3, pollInterval = 1000) {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await authService.getSessionStatus()

        if (response.data.has_purchased_pack) {
          hasPurchased.value = true
          paymentStatus.value = 'success'
          return true
        }

        // Wait before next attempt (unless this was the last attempt)
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, pollInterval))
        }

      } catch (error) {
        console.error(`Payment status check failed (attempt ${attempt}):`, error)

        // Continue polling even if one attempt fails
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, pollInterval))
        }
      }
    }

    // All attempts exhausted without confirming payment
    paymentStatus.value = 'pending'
    return false
  }

  /**
   * Load Appeal Data - Fetch property and comp data
   * Only available after purchase
   */
  async function loadAppealData() {
    try {
      const response = await paymentService.getAppealData()
      appealData.value = response.data.appeal_data || []
      return appealData.value
    } catch (error) {
      console.error('Failed to load appeal data:', error)
      throw error
    }
  }

  /**
   * Download PDF - Generate and download appeal pack PDF
   * Only available after purchase
   */
  async function downloadPDF() {
    isDownloadingPDF.value = true

    try {
      console.log('Starting PDF download...')
      const response = await paymentService.downloadPDF()
      console.log('PDF download response received:', response.status, response.headers['content-type'])

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' })
      console.log('Blob created, size:', blob.size, 'bytes')

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'HummingBird_Appeal_Pack.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      console.log('PDF download triggered successfully')

    } catch (error) {
      console.error('Failed to download PDF:', error)
      console.error('Error response:', error.response)
      console.error('Error status:', error.response?.status)
      console.error('Error data:', error.response?.data)

      // Parse blob error response to get actual error message
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text()
          const errorData = JSON.parse(text)
          console.error('Parsed error detail:', errorData.detail)
          // Create a new error with the parsed detail
          const enhancedError = new Error(errorData.detail || error.message)
          enhancedError.response = {
            ...error.response,
            data: errorData
          }
          throw enhancedError
        } catch (parseError) {
          console.error('Could not parse error blob:', parseError)
          throw error
        }
      }

      throw error
    } finally {
      isDownloadingPDF.value = false
    }
  }

  /**
   * Reset Payment Status - Clear payment state
   * Used when navigating away from payment result pages
   */
  function resetPaymentStatus() {
    paymentStatus.value = null
  }

  // ==================== RETURN (PUBLIC API) ====================

  return {
    // State
    hasPurchased,
    paymentStatus,
    appealData,
    isDownloadingPDF,
    eligibilityReason,
    eligibilityComps,
    isCheckingEligibility,

    // Actions
    checkEligibility,
    createCheckout,
    pollPaymentStatus,
    loadAppealData,
    downloadPDF,
    resetPaymentStatus
  }
})
